import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Brain, Activity, BedDouble, AlertTriangle, Users,
  ArrowUpRight, ArrowDownRight, Clock, CheckCircle,
  TrendingUp, DollarSign, Bell, ChevronRight, Radio,
  LogIn, LogOut, CreditCard, Loader2, Sparkles,
  ShieldCheck, ShieldAlert, ThumbsUp, ThumbsDown,
  Send, RefreshCw, Lightbulb, Lock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';
import TerminalFloorNav from '../components/TerminalFloorNav';

/* ── Glanceable KPI ── */
function Kpi({ label, value, sub, icon: Icon, color, trend }) {
  return (
    <div className="flex items-center gap-3 bg-[#0d1220] border border-[#1a2235] rounded-xl px-4 py-3">
      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", color || "bg-blue-500/10")}>
        <Icon className="w-5 h-5 text-inherit" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold font-mono leading-none tracking-tight">{value}</p>
        <p className="text-[10px] text-[#64748b] mt-0.5 truncate">{label}</p>
      </div>
      {trend && (
        <div className={cn("ml-auto text-[10px] font-mono flex items-center gap-0.5", trend > 0 ? "text-emerald-400" : "text-red-400")}>
          {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      )}
      {sub && !trend && <span className="ml-auto text-[10px] text-[#475569] font-mono">{sub}</span>}
    </div>
  );
}

/* ── Alert Row ── */
function AlertRow({ alert }) {
  const colors = { high: 'border-red-500 bg-red-500/5', medium: 'border-amber-500 bg-amber-500/5', low: 'border-blue-500 bg-blue-500/5' };
  const dots = { high: 'bg-red-500', medium: 'bg-amber-500', low: 'bg-blue-500' };
  return (
    <div className={cn("border-l-2 rounded-r-lg px-3 py-2 flex items-center gap-2", colors[alert.priority])}>
      <div className={cn("w-1.5 h-1.5 rounded-full shrink-0", dots[alert.priority], alert.priority === 'high' && "animate-pulse")} />
      <p className="text-[11px] flex-1 leading-snug">{alert.message}</p>
      <Button size="sm" variant="ghost" className="h-5 text-[9px] text-blue-400 px-1.5 shrink-0">
        {alert.action} <ChevronRight className="w-2.5 h-2.5" />
      </Button>
    </div>
  );
}

/* ── Room Cell ── */
function RoomCell({ room }) {
  const styles = {
    occupied: 'bg-blue-500/15 border-blue-500/25 text-blue-300',
    available: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  };
  return (
    <div className={cn("border rounded-lg p-1.5 text-center cursor-pointer transition-all hover:scale-105", styles[room.status])} title={room.guest_name || room.type}>
      <p className="text-[11px] font-bold font-mono">{room.room_number}</p>
      {room.status === 'occupied' && <p className="text-[8px] truncate opacity-70">{room.guest_name?.split(' ')[0]}</p>}
      {room.status === 'available' && <p className="text-[8px] font-mono">${room.price_per_night}</p>}
    </div>
  );
}

/* ── Occupancy Ring ── */
function OccupancyRing({ pct }) {
  const r = 42, c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const color = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative w-28 h-28 shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#1a2235" strokeWidth="6" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold font-mono" style={{ color }}>{pct}%</span>
        <span className="text-[8px] text-[#64748b] uppercase tracking-wider">Occupancy</span>
      </div>
    </div>
  );
}

/* ── AI Insight Briefing ── */
function InsightCard({ insight, onRequestApproval, onRefresh, loading }) {
  if (!insight) return null;
  const { briefing, recommendations, pending_approvals } = insight;
  const pendingIds = new Set((pending_approvals || []).filter(a => a.status === 'pending').map(a => a.recommendation_id));
  const approvedIds = new Set((pending_approvals || []).filter(a => a.decision === 'approved').map(a => a.recommendation_id));
  const rejectedIds = new Set((pending_approvals || []).filter(a => a.decision === 'rejected').map(a => a.recommendation_id));

  return (
    <div className="bg-gradient-to-r from-[#0d1220] via-[#111a30] to-[#0d1220] border border-blue-500/20 rounded-xl p-4" data-testid="marina-insights">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <h2 className="text-xs font-bold uppercase tracking-wider text-blue-400">Marina AI Briefing</h2>
          <Badge className="text-[7px] bg-blue-500/10 text-blue-300 border-blue-500/20">GPT-5.2</Badge>
        </div>
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading}
          className="h-6 text-[9px] text-[#64748b] hover:text-blue-400 px-2" data-testid="refresh-insights">
          <RefreshCw className={cn("w-3 h-3 mr-1", loading && "animate-spin")} />
          {loading ? 'Thinking...' : 'Refresh'}
        </Button>
      </div>

      {/* Briefing text */}
      <p className="text-[12px] leading-relaxed text-[#cbd5e1] mb-3" data-testid="insight-briefing">{briefing}</p>

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="space-y-2">
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#64748b]">Recommendations</p>
          {recommendations.map((rec) => {
            const isPending = pendingIds.has(rec.id);
            const isApproved = approvedIds.has(rec.id);
            const isRejected = rejectedIds.has(rec.id);

            return (
              <div key={rec.id}
                className={cn(
                  "rounded-lg px-3 py-2.5 flex items-start gap-3 border transition-all",
                  rec.requires_approval
                    ? "bg-amber-500/5 border-amber-500/15"
                    : "bg-[#0d1220] border-[#1a2235]"
                )}
                data-testid={`recommendation-${rec.id}`}
              >
                <div className={cn("w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5",
                  rec.requires_approval ? "bg-amber-500/10" : "bg-emerald-500/10"
                )}>
                  {rec.requires_approval ? <Lock className="w-3 h-3 text-amber-400" /> : <Lightbulb className="w-3 h-3 text-emerald-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[11px] font-semibold">{rec.title}</p>
                    <Badge className={cn("text-[7px] h-3.5",
                      rec.urgency === 'high' ? 'bg-red-500/10 text-red-400' :
                      rec.urgency === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                      'bg-blue-500/10 text-blue-400'
                    )}>
                      {rec.urgency}
                    </Badge>
                    {rec.requires_approval && (
                      <Badge className="text-[7px] h-3.5 bg-amber-500/10 text-amber-400 border-amber-500/20">
                        <ShieldAlert className="w-2 h-2 mr-0.5" /> Needs Approval
                      </Badge>
                    )}
                  </div>
                  <p className="text-[10px] text-[#94a3b8] mt-0.5 leading-snug">{rec.description}</p>
                </div>

                {/* Approval actions */}
                <div className="shrink-0 flex items-center gap-1.5 mt-0.5">
                  {rec.requires_approval && !isPending && !isApproved && !isRejected && (
                    <Button size="sm" onClick={() => onRequestApproval(rec)}
                      className="h-6 text-[8px] bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/20 px-2"
                      data-testid={`request-approval-${rec.id}`}>
                      <Send className="w-2.5 h-2.5 mr-1" /> Request Sage Approval
                    </Button>
                  )}
                  {isPending && (
                    <Badge className="text-[8px] bg-amber-500/10 text-amber-400 animate-pulse" data-testid={`pending-${rec.id}`}>
                      <Clock className="w-2.5 h-2.5 mr-1" /> Awaiting Sage
                    </Badge>
                  )}
                  {isApproved && (
                    <Badge className="text-[8px] bg-emerald-500/10 text-emerald-400" data-testid={`approved-${rec.id}`}>
                      <ShieldCheck className="w-2.5 h-2.5 mr-1" /> Sage Approved
                    </Badge>
                  )}
                  {isRejected && (
                    <Badge className="text-[8px] bg-red-500/10 text-red-400" data-testid={`rejected-${rec.id}`}>
                      <ThumbsDown className="w-2.5 h-2.5 mr-1" /> Rejected
                    </Badge>
                  )}
                  {!rec.requires_approval && (
                    <Badge className="text-[8px] bg-emerald-500/10 text-emerald-400">
                      <CheckCircle className="w-2.5 h-2.5 mr-1" /> Auto-OK
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pending Approvals from Sage */}
      {pending_approvals && pending_approvals.filter(a => a.status === 'pending').length > 0 && (
        <div className="mt-3 pt-2 border-t border-[#1a2235]">
          <p className="text-[9px] font-bold uppercase tracking-wider text-amber-400/70 mb-1.5">
            Pending Sage Decisions ({pending_approvals.filter(a => a.status === 'pending').length})
          </p>
          {pending_approvals.filter(a => a.status === 'pending').map((a) => (
            <div key={a.id} className="flex items-center gap-2 py-1 text-[10px] text-[#94a3b8]">
              <Clock className="w-3 h-3 text-amber-400 shrink-0" />
              <span className="truncate">{a.title}</span>
              <span className="ml-auto text-[8px] text-[#475569] font-mono">{a.requested_at?.slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Sage Approval Panel (for managers/owners) ── */
function SageApprovalPanel({ hotelId, approvals, onRespond }) {
  const pending = (approvals || []).filter(a => a.status === 'pending');
  if (pending.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-[#0d1220] via-[#1a1225] to-[#0d1220] border border-purple-500/20 rounded-xl p-4" data-testid="sage-approval-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
          <ShieldCheck className="w-3.5 h-3.5 text-purple-400" />
        </div>
        <h2 className="text-xs font-bold uppercase tracking-wider text-purple-400">Sage Approval Queue</h2>
        <Badge className="text-[7px] bg-purple-500/10 text-purple-300">{pending.length} pending</Badge>
      </div>
      <div className="space-y-2">
        {pending.map((a) => (
          <div key={a.id} className="bg-[#0d1220] border border-[#1a2235] rounded-lg px-3 py-2.5 flex items-center gap-3" data-testid={`sage-item-${a.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold">{a.title}</p>
              <p className="text-[9px] text-[#64748b] mt-0.5">{a.description}</p>
              <p className="text-[8px] text-[#475569] mt-0.5 font-mono">Requested by Marina AI · {a.requested_at?.slice(0, 16).replace('T', ' ')}</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" onClick={() => onRespond(a.id, 'approved')}
                className="h-7 text-[9px] bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/20 px-2.5"
                data-testid={`approve-${a.id}`}>
                <ThumbsUp className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button size="sm" onClick={() => onRespond(a.id, 'rejected')}
                className="h-7 text-[9px] bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 px-2.5"
                data-testid={`reject-${a.id}`}>
                <ThumbsDown className="w-3 h-3 mr-1" /> Reject
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MarinaDashboardPreview({ hotelId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [approvals, setApprovals] = useState([]);

  const hid = hotelId || 'hotel_grand_marina';

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/marina/${hid}/dashboard`);
      const json = await res.json();
      setData(json);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hid]);

  const fetchInsights = useCallback(async () => {
    setInsightLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/marina/${hid}/insights`);
      const json = await res.json();
      setInsight(json);
      setApprovals(json.pending_approvals || []);
    } catch { /* ignore */ }
    finally { setInsightLoading(false); }
  }, [hid]);

  const fetchApprovals = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/marina/${hid}/insights/approvals`);
      const json = await res.json();
      setApprovals(json.approvals || []);
      // Also update insight pending_approvals if insight exists
      if (insight) {
        setInsight(prev => prev ? { ...prev, pending_approvals: json.approvals || [] } : prev);
      }
    } catch { /* ignore */ }
  }, [hid, insight]);

  const requestApproval = async (rec) => {
    try {
      await fetch(`${BACKEND_URL}/api/marina/${hid}/insights/request-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recommendation_id: rec.id,
          title: rec.title,
          description: rec.description,
          category: rec.category,
          suggested_value: null,
        }),
      });
      await fetchApprovals();
    } catch { /* ignore */ }
  };

  const respondToApproval = async (approvalId, decision) => {
    try {
      await fetch(`${BACKEND_URL}/api/marina/${hid}/insights/approvals/${approvalId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, responder: 'sage', note: '' }),
      });
      await fetchApprovals();
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 30000); return () => clearInterval(iv); }, [fetchData]);
  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#64748b]">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-mono">Marina is loading...</span>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { kpis, room_grid, alerts, actions_log, today_arrivals, today_departures } = data;

  return (
    <div className="terminal-theme min-h-screen" data-testid="marina-dashboard">
      {/* ── Top Bar ── */}
      <div className="border-b border-[#1a2235] bg-[#0a0f1d]/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                <Brain className="w-4.5 h-4.5 text-blue-400" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#0a0f1d]" />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight" data-testid="marina-title">Marina AI</h1>
              <p className="text-[9px] text-[#475569]">{data.hotel_name}</p>
            </div>
            <Badge className="ml-2 text-[9px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
              <Radio className="w-2.5 h-2.5 mr-1 animate-pulse" /> Live
            </Badge>
          </div>

          <div className="flex items-center gap-6 text-center">
            <div>
              <p className="text-lg font-bold font-mono text-blue-400 leading-none">{kpis.today_checkins}</p>
              <p className="text-[8px] text-[#475569] uppercase">Arrivals</p>
            </div>
            <div className="w-px h-6 bg-[#1a2235]" />
            <div>
              <p className="text-lg font-bold font-mono text-amber-400 leading-none">{kpis.today_checkouts}</p>
              <p className="text-[8px] text-[#475569] uppercase">Departures</p>
            </div>
            <div className="w-px h-6 bg-[#1a2235]" />
            <div>
              <p className="text-lg font-bold font-mono text-red-400 leading-none">{kpis.pending_confirmations}</p>
              <p className="text-[8px] text-[#475569] uppercase">Pending</p>
            </div>
            <div className="w-px h-6 bg-[#1a2235]" />
            <div>
              <p className="text-lg font-bold font-mono text-emerald-400 leading-none">${(kpis.today_revenue || 0).toLocaleString()}</p>
              <p className="text-[8px] text-[#475569] uppercase">Today Rev</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="max-w-[1800px] mx-auto p-4 space-y-4">

        {/* Marina AI Insight Briefing */}
        <InsightCard insight={insight} onRequestApproval={requestApproval} onRefresh={fetchInsights} loading={insightLoading} />

        {/* Sage Approval Queue (visible to managers/owners) */}
        <SageApprovalPanel hotelId={hid} approvals={approvals} onRespond={respondToApproval} />

        {/* Row 1: KPIs + Occupancy Ring */}
        <div className="flex gap-4 items-stretch">
          {/* Occupancy Ring */}
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl p-4 flex items-center justify-center">
            <OccupancyRing pct={kpis.occupancy_pct} />
          </div>
          {/* KPI Grid */}
          <div className="flex-1 grid grid-cols-2 xl:grid-cols-4 gap-3">
            <Kpi label="Rooms Occupied" value={kpis.occupied_rooms} sub={`/ ${kpis.total_rooms}`} icon={BedDouble} color="bg-blue-500/10 text-blue-400" />
            <Kpi label="Available" value={kpis.available_rooms} icon={CheckCircle} color="bg-emerald-500/10 text-emerald-400" />
            <Kpi label="Avg Rate" value={`$${kpis.avg_rate}`} icon={DollarSign} color="bg-[#F7931A]/10 text-[#F7931A]" />
            <Kpi label="Total Revenue" value={`$${(kpis.total_revenue || 0).toLocaleString()}`} sub={`${kpis.total_bookings} bookings`} icon={TrendingUp} color="bg-emerald-500/10 text-emerald-400" />
          </div>
        </div>

        {/* Row 2: Room Grid + Alerts + Actions */}
        <div className="grid grid-cols-12 gap-4">

          {/* Room Grid */}
          <div className="col-span-12 lg:col-span-5 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="room-grid">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <BedDouble className="w-3.5 h-3.5" /> Room Grid
              </h2>
              <div className="flex gap-3 text-[8px] text-[#475569]">
                <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-sm bg-blue-500/50" /> Occupied ({kpis.occupied_rooms})</span>
                <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-sm bg-emerald-500/40" /> Available ({kpis.available_rooms})</span>
              </div>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
              {room_grid.map((r) => <RoomCell key={r.room_id} room={r} />)}
            </div>
          </div>

          {/* Alerts */}
          <div className="col-span-12 lg:col-span-4 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="alerts-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <Bell className="w-3.5 h-3.5" /> Alerts
              </h2>
              {alerts.filter(a => a.priority === 'high').length > 0 && (
                <Badge className="text-[9px] bg-red-500/10 text-red-400 animate-pulse">
                  {alerts.filter(a => a.priority === 'high').length} urgent
                </Badge>
              )}
            </div>
            <div className="space-y-2">
              {alerts.length === 0 ? (
                <p className="text-xs text-[#475569] text-center py-6">All clear — no alerts right now</p>
              ) : (
                alerts.map((a, i) => <AlertRow key={i} alert={a} />)
              )}
            </div>
          </div>

          {/* Actions Log */}
          <div className="col-span-12 lg:col-span-3 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="actions-log">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5 mb-3">
              <Activity className="w-3.5 h-3.5" /> Recent Activity
            </h2>
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {actions_log.map((a, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5 border-b border-[#1a2235]/60 last:border-0">
                  <span className="text-[9px] font-mono text-[#475569] w-12 shrink-0 pt-0.5">{a.time?.slice(11, 16) || ''}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] leading-snug truncate">{a.action}</p>
                    <Badge className={cn("text-[7px] h-3.5 mt-0.5", a.status === 'confirmed' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400')}>
                      {a.status === 'confirmed' ? <CheckCircle className="w-2 h-2 mr-0.5" /> : <Clock className="w-2 h-2 mr-0.5" />}
                      {a.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: Today's Arrivals + Departures + Booking Source */}
        <div className="grid grid-cols-12 gap-4">
          {/* Arrivals */}
          <div className="col-span-12 md:col-span-4 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="today-arrivals">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5 mb-3">
              <LogIn className="w-3.5 h-3.5 text-blue-400" /> Today's Arrivals ({today_arrivals.length})
            </h2>
            {today_arrivals.length === 0 ? (
              <p className="text-[11px] text-[#475569] text-center py-4">No arrivals today</p>
            ) : (
              <div className="space-y-1.5">
                {today_arrivals.map((a, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#080c16] rounded-lg px-3 py-2">
                    <div>
                      <p className="text-xs font-medium">{a.guest}</p>
                      <p className="text-[9px] text-[#64748b]">{a.room_type} | {a.guests} guest(s)</p>
                    </div>
                    <Badge className={cn("text-[8px]", a.payment === 'confirmed' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400')}>
                      {a.payment === 'confirmed' ? <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> : <CreditCard className="w-2.5 h-2.5 mr-0.5" />}
                      {a.payment}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Departures */}
          <div className="col-span-12 md:col-span-4 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="today-departures">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#94748b] flex items-center gap-1.5 mb-3">
              <LogOut className="w-3.5 h-3.5 text-amber-400" /> Today's Departures ({today_departures.length})
            </h2>
            {today_departures.length === 0 ? (
              <p className="text-[11px] text-[#475569] text-center py-4">No departures today</p>
            ) : (
              <div className="space-y-1.5">
                {today_departures.map((d, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#080c16] rounded-lg px-3 py-2">
                    <div>
                      <p className="text-xs font-medium">{d.guest}</p>
                      <p className="text-[9px] text-[#64748b]">{d.room_type}</p>
                    </div>
                    <Badge variant="outline" className="text-[8px] border-[#1a2235] text-[#64748b]">Checkout</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Booking Sources */}
          <div className="col-span-12 md:col-span-4 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="booking-sources">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5 mb-3">
              <Users className="w-3.5 h-3.5" /> Booking Sources
            </h2>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-[#94a3b8]">Direct (getbitsy)</span>
                  <span className="font-mono font-bold text-blue-400">{kpis.direct_bookings}</span>
                </div>
                <div className="h-2 bg-[#1a2235] rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500/70 rounded-full" style={{ width: `${kpis.total_bookings ? (kpis.direct_bookings / kpis.total_bookings) * 100 : 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-[#94a3b8]">OTA (Channex)</span>
                  <span className="font-mono font-bold text-amber-400">{kpis.ota_bookings}</span>
                </div>
                <div className="h-2 bg-[#1a2235] rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500/70 rounded-full" style={{ width: `${kpis.total_bookings ? (kpis.ota_bookings / kpis.total_bookings) * 100 : 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-[#94a3b8]">Crypto</span>
                  <span className="font-mono font-bold text-emerald-400">{kpis.crypto_bookings}</span>
                </div>
                <div className="h-2 bg-[#1a2235] rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500/70 rounded-full" style={{ width: `${kpis.total_bookings ? (kpis.crypto_bookings / kpis.total_bookings) * 100 : 0}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TerminalFloorNav />
    </div>
  );
}
